import React, { useState } from 'react';
import { 
  Database, 
  Trash2, 
  TestTube, 
  Settings, 
  Activity,
  CheckCircle,
  XCircle,
  Loader2,
  Clock,
  Zap
} from 'lucide-react';

export default function ConnectionCard({ connection, onDelete, onTest, onNormalize }) {
  const [loading, setLoading] = useState(false);
  const [normalizing, setNormalizing] = useState(false);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-green-400 bg-green-500/20';
      case 'error':
        return 'text-red-400 bg-red-500/20';
      case 'testing':
        return 'text-yellow-400 bg-yellow-500/20';
      default:
        return 'text-slate-400 bg-slate-500/20';
    }
  };

  const getDbIcon = (dbType) => {
    switch (dbType) {
      case 'postgresql':
        return '🐘';
      case 'mysql':
        return '🐬';
      case 'sqlserver':
        return '🗄️';
      case 'oracle':
        return '🔶';
      default:
        return '💾';
    }
  };

  const getDbName = (dbType) => {
    switch (dbType) {
      case 'postgresql':
        return 'PostgreSQL';
      case 'mysql':
        return 'MySQL';
      case 'sqlserver':
        return 'SQL Server';
      case 'oracle':
        return 'Oracle';
      default:
        return dbType;
    }
  };

  const handleTest = async () => {
    setLoading(true);
    await onTest(connection.id);
    setLoading(false);
  };

  const handleNormalize = async () => {
    setNormalizing(true);
    await onNormalize(connection.id);
    setNormalizing(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6 hover:border-slate-600 transition-all">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start space-x-3 flex-1">
          <div className="text-3xl">{getDbIcon(connection.db_type)}</div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-white truncate">{connection.name}</h3>
            <p className="text-sm text-slate-400 truncate">
              {getDbName(connection.db_type)} • {connection.host}:{connection.port}
            </p>
            {connection.description && (
              <p className="text-xs text-slate-500 mt-1 line-clamp-2">{connection.description}</p>
            )}
          </div>
        </div>

        {/* Status Badge */}
        <div className={`flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(connection.status)}`}>
          {connection.status === 'active' && <CheckCircle className="w-3 h-3" />}
          {connection.status === 'error' && <XCircle className="w-3 h-3" />}
          {connection.status === 'testing' && <Loader2 className="w-3 h-3 animate-spin" />}
          <span className="capitalize">{connection.status}</span>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="bg-slate-700/30 rounded-lg p-3">
          <div className="flex items-center space-x-2 text-slate-400 mb-1">
            <Activity className="w-3 h-3" />
            <span className="text-xs">Latency</span>
          </div>
          <div className="text-lg font-semibold text-white">
            {connection.test_latency_ms ? `${connection.test_latency_ms}ms` : 'N/A'}
          </div>
        </div>

        <div className="bg-slate-700/30 rounded-lg p-3">
          <div className="flex items-center space-x-2 text-slate-400 mb-1">
            <Database className="w-3 h-3" />
            <span className="text-xs">Database</span>
          </div>
          <div className="text-sm font-semibold text-white truncate">
            {connection.database_name}
          </div>
        </div>

        <div className="bg-slate-700/30 rounded-lg p-3">
          <div className="flex items-center space-x-2 text-slate-400 mb-1">
            <Clock className="w-3 h-3" />
            <span className="text-xs">Last Test</span>
          </div>
          <div className="text-sm font-semibold text-white">
            {formatDate(connection.last_tested_at)}
          </div>
        </div>
      </div>

      {/* Connection Method Badge */}
      <div className="mb-4">
        <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded text-xs ${
          connection.connection_method === 'ssh_tunnel'
            ? 'bg-purple-500/20 text-purple-400'
            : 'bg-blue-500/20 text-blue-400'
        }`}>
          {connection.connection_method === 'ssh_tunnel' ? '🔐 SSH Tunnel' : '🌐 Direct'}
        </span>
        {connection.read_only && (
          <span className="ml-2 inline-flex items-center space-x-1 px-2 py-1 rounded text-xs bg-green-500/20 text-green-400">
            ✓ Read-Only
          </span>
        )}
        {connection.ssl_enabled && (
          <span className="ml-2 inline-flex items-center space-x-1 px-2 py-1 rounded text-xs bg-blue-500/20 text-blue-400">
            🔒 SSL/TLS
          </span>
        )}
        {connection.is_normalized && (
          <span className="ml-2 inline-flex items-center space-x-1 px-2 py-1 rounded text-xs bg-green-500/20 text-green-400">
            ⚡ Normalized
          </span>
        )}
        {!connection.is_normalized && (
          <span className="ml-2 inline-flex items-center space-x-1 px-2 py-1 rounded text-xs bg-yellow-500/20 text-yellow-400">
            ⚠️ Not Normalized
          </span>
        )}
      </div>

      {/* Error Message */}
      {connection.last_error && connection.status === 'error' && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4">
          <p className="text-xs text-red-400">{connection.last_error}</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center space-x-2">
        <button
          onClick={handleTest}
          disabled={loading}
          className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Testing...</span>
            </>
          ) : (
            <>
              <TestTube className="w-4 h-4" />
              <span>Test</span>
            </>
          )}
        </button>

        <button
          onClick={handleNormalize}
          disabled={normalizing}
          className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="Normalize data (calculate percentiles & map schema)"
        >
          {normalizing ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Normalizing...</span>
            </>
          ) : (
            <>
              <Zap className="w-4 h-4" />
              <span>Normalize</span>
            </>
          )}
        </button>

        <button
          className="flex items-center justify-center space-x-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm transition-colors"
          title="Configure"
        >
          <Settings className="w-4 h-4" />
        </button>

        <button
          onClick={() => onDelete(connection.id)}
          className="flex items-center justify-center space-x-2 px-4 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg text-sm transition-colors"
          title="Delete"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
